import { archiveRecordedMeeting, notifyMeetingsChanged } from './meetingArchive';
import { safeRecordingPath } from './recordings';
import { isS3Configured } from './storage/s3';
import { sendSummaryEmail } from './mailer';
import { getAuthState, requireUserId } from './supabase/auth';
import { resolveParticipants } from './supabase/directoryRepo';
import { ensurePrefsSynced } from './prefsSync';
import {
  createBotMeeting,
  deleteMeetings,
  findBotMeetingForEvent,
  getMeetingShareInfo,
  listActiveBotMeetings,
  listBotClientTasks,
  listPendingArchives,
  patchOptions,
  type BotClientTasks,
} from './supabase/meetingsRepo';
import { createBot, detectService, stopBot, type SkribbyBot } from './skribby';
import { contentSealer } from './crypto/sealContent';
import { normalizeMeetingUrl } from '../shared/meetingLink';
import type { BotSendRequest, SummaryEmailPayload } from '../shared/types';
import { t } from './language';

/**
 * Bot toplantıları — masaüstü tarafı.
 *
 * Botun durumunu izlemek ve biten toplantıyı tamamlamak (indir → transkript →
 * özet → bulut) artık TalkNote sunucusunda (web/lib/jobs/bots.ts), uygulama
 * kapalıyken de. Burada kalanlar:
 *  - bot gönderme / durdurma (sunucu API'si üzerinden),
 *  - sunucunun güncellediği durumlar değişince arayüzü tazeleme,
 *  - sunucunun bıraktığı işler (`options.clientTasks`): katılımcıları kişi
 *    dizinine bağlama ve özet e-postası (sunucudan, kendi alan adımızdan),
 *  - yarıda kalmış "Kayıt Al" arşivlerini yeniden deneme,
 *  - özet tercihlerinin sunucu kopyası (oturum başına bir kez).
 */

const POLL_MS = 30_000;

// ————————————————————————————————————————————————
// Bot gönderme / durdurma (IPC'den çağrılır)
// ————————————————————————————————————————————————

export async function sendBot(request: BotSendRequest): Promise<{ meetingId: string }> {
  const meetingUrl = normalizeMeetingUrl(request?.meetingUrl) ?? '';
  const service = detectService(meetingUrl);
  if (!service) {
    throw new Error(
      t(
        'Yalnızca Google Meet, Zoom ve Microsoft Teams linkleri destekleniyor.',
        'Only Google Meet, Zoom and Microsoft Teams links are supported.'
      )
    );
  }

  const startISO = request.startISO && Number.isFinite(Date.parse(request.startISO))
    ? request.startISO
    : new Date().toISOString();
  const host = new URL(meetingUrl).hostname;
  const title = request.title?.trim() || t(`Bot kaydı · ${host}`, `Bot recording · ${host}`);
  const userId = await requireUserId();
  const calendarEventId =
    typeof request.calendarEventId === 'string' && request.calendarEventId ? request.calendarEventId : undefined;

  // Aynı takvim etkinliğine ikinci bot gönderme (elle + otomatik çakışması).
  if (calendarEventId) {
    const existing = await findBotMeetingForEvent(calendarEventId);
    if (existing) throw new Error(t('Bu toplantıya zaten bir bot gönderildi.', 'A bot has already been sent to this meeting.'));
  }

  const meetingId = await createBotMeeting({
    title,
    meetingUrl,
    recordedAtISO: startISO,
    participants: Array.isArray(request.participants) ? request.participants : [],
    calendarEventId,
  });

  let bot: SkribbyBot;
  try {
    bot = await createBot({
      meetingUrl,
      service,
      scheduledStart: new Date(startISO),
      metadata: { meeting_id: meetingId, user_id: userId },
    });
  } catch (err) {
    // Toplantıyı sunucu "başarısız" işaretliyor (durum kolonu yalnızca
    // sunucuya yazılabilir, 0027); sunucuya hiç ulaşılamadıysa bot durumu
    // en azından arayüzde başarısız görünsün.
    await patchOptions(meetingId, (options) => ({ ...options, bot: { status: 'failed', meetingUrl } })).catch(() => {});
    notifyMeetingsChanged();
    throw err;
  }

  await patchOptions(meetingId, (options) => ({
    ...options,
    bot: { id: bot.id, status: bot.status, meetingUrl },
  }));

  notifyMeetingsChanged();
  triggerBotSync();
  return { meetingId };
}

/**
 * Bir takvim etkinliği otomatik katılımdan çıkarıldığında: bot henüz
 * toplantıya girmediyse durdurulur ve yer tutucu toplantı kaldırılır.
 */
export async function cancelBotForEvent(calendarEventId: string): Promise<void> {
  const meetingId = await findBotMeetingForEvent(calendarEventId);
  if (!meetingId || meetingId === 'unknown') return;

  const active = (await listActiveBotMeetings()).find((m) => m.meetingId === meetingId);
  if (!active) return; // bot zaten bitti — kayıt kullanıcıya kalsın

  const notJoinedYet = ['creating', 'scheduled', 'booting', 'joining'].includes(active.bot.status);
  if (!notJoinedYet) return; // toplantıdaysa kararı kullanıcı "Botu çıkar" ile verir

  if (active.bot.id) {
    await stopBot(active.bot.id).catch((err) => console.error('[botSync] bot durdurulamadı:', err));
  }
  await deleteMeetings([meetingId]).catch((err) => console.error('[botSync] yer tutucu silinemedi:', err));
  notifyMeetingsChanged();
}

export async function stopMeetingBot(meetingId: string): Promise<void> {
  const active = (await listActiveBotMeetings()).find((m) => m.meetingId === meetingId);
  if (!active?.bot.id) throw new Error(t('Bu toplantıda çalışan bir bot yok.', 'There is no running bot in this meeting.'));
  await stopBot(active.bot.id);
  triggerBotSync();
}

// ————————————————————————————————————————————————
// Senkron döngüsü
// ————————————————————————————————————————————————

let timer: NodeJS.Timeout | null = null;
let running = false;
let rerunRequested = false;

export function startBotSync(): void {
  if (timer) return;
  timer = setInterval(() => void tick(), POLL_MS);
  void tick();
}

/** Hemen bir tur çalıştır (ör. bot gönderildikten ya da giriş yapıldıktan sonra). */
export function triggerBotSync(): void {
  void tick();
}

async function tick(): Promise<void> {
  if (running) {
    rerunRequested = true;
    return;
  }
  running = true;
  try {
    const auth = await getAuthState().catch(() => null);
    if (!auth?.signedIn) return;

    await ensurePrefsSynced().catch((err) => console.error('[botSync] tercihler senkronlanamadı:', err));
    await syncBots();
    await runClientTasks();
    await retryPendingArchives();
  } catch (err) {
    console.error('[botSync] tur başarısız:', err);
  } finally {
    running = false;
    if (rerunRequested) {
      rerunRequested = false;
      void tick();
    }
  }
}

/** Son görülen bot durumları — değişince arayüz tazelenir. */
let lastSnapshot = '';

async function syncBots(): Promise<void> {
  const active = await listActiveBotMeetings();
  const snapshot = active.map((m) => `${m.meetingId}:${m.bot.status}`).sort().join('|');
  if (snapshot !== lastSnapshot) {
    // Bir toplantı listeden çıktıysa sunucu tamamladı ya da başarısız işaretledi.
    lastSnapshot = snapshot;
    notifyMeetingsChanged();
  }
}

/** Geçici hatada (ağ, 429, 5xx) en fazla bu kadar deneme; aralık 2, 4, 8, 16 dk. */
const MAX_CLIENT_TASK_ATTEMPTS = 5;

/**
 * Sunucunun tamamladığı bot toplantılarında masaüstüne kalan işler. İş notu
 * yalnızca işler bitince silinir; geçici bir hatada (sunucu/ağ) not kalır ve
 * artan aralıklarla yeniden denenir (BUG-05 — eskiden not önce siliniyor,
 * başarısız e-posta kayboluyordu). Katılımcılara ikinci e-posta gitmez:
 * sunucu aynı toplantıya 24 saatte bir kez gönderir (`already_sent`).
 */
async function runClientTasks(): Promise<void> {
  for (const item of await listBotClientTasks()) {
    const tasks = item.tasks;
    if (tasks.nextAttemptAt && Date.parse(tasks.nextAttemptAt) > Date.now()) continue;

    const remaining = await applyClientTasks(item.meetingId, tasks);
    const attempts = (tasks.attempts ?? 0) + 1;
    const retry = remaining && attempts < MAX_CLIENT_TASK_ATTEMPTS;
    if (remaining && !retry) console.warn('[botSync] işler bırakıldı, deneme sınırı:', item.meetingId);
    try {
      // Okurken çözülen özet kopyası ve katılımcılar, şifreleme açıksa geri yazılmadan önce yeniden şifrelenir.
      const sealer = await contentSealer(await requireUserId(), item.meetingId);
      const summaryEmail = retry && remaining?.summaryEmail
        ? ((await sealer.json('meetings.summary_email', remaining.summaryEmail)) as BotClientTasks['summaryEmail'])
        : undefined;
      const participants = retry && remaining?.participants
        ? ((await sealer.json('meetings.participants', remaining.participants)) as BotClientTasks['participants'])
        : undefined;
      await patchOptions(item.meetingId, (options) => {
        const next = { ...options };
        // Daha düşük seviyede (ya da düz) geri yazıldıysa "şifreli" işareti de iner
        // (dönüştürme işi toplantıyı yeniden ele alır).
        if (retry && (summaryEmail || participants) && next.enc) {
          if (!sealer.enabled) delete next.enc;
          else if (next.enc > sealer.level) next.enc = sealer.level as 1 | 2 | 3;
        }
        if (retry) {
          next.clientTasks = {
            ...remaining,
            ...(summaryEmail ? { summaryEmail } : {}),
            ...(participants ? { participants } : {}),
            attempts,
            nextAttemptAt: new Date(Date.now() + 2 ** attempts * 60_000).toISOString(),
          };
        } else {
          delete next.clientTasks;
        }
        return next;
      });
    } catch (err) {
      console.error('[botSync] iş notu güncellenemedi:', item.meetingId, err);
    }
  }
}

/** İşleri yapar; geçici hatayla yapılamayanları döndürür (hepsi bittiyse null). */
async function applyClientTasks(meetingId: string, tasks: BotClientTasks): Promise<BotClientTasks | null> {
  const remaining: BotClientTasks = {};

  // Takvimden gelen katılımcılar (e-postalı) korunur; bottan gelen isimler eklenir.
  if (tasks.participants?.length) {
    const linked = await resolveParticipants(meetingId, tasks.participants, 'merge').then(
      () => true,
      (err) => {
        console.error('[botSync] katılımcılar bağlanamadı:', meetingId, err);
        return false;
      }
    );
    if (linked) notifyMeetingsChanged();
    else remaining.participants = tasks.participants;
  }

  const email = tasks.summaryEmail;
  if (email) {
    try {
      const info = await getMeetingShareInfo(meetingId);
      const minutes = Math.max(1, Math.round(email.durationSeconds / 60));
      const payload: SummaryEmailPayload = {
        title: email.title,
        recordedAtISO: info?.recordedAtISO ?? new Date(Date.now() - email.durationSeconds * 1000).toISOString(),
        durationLabel: t(`${minutes} dk`, `${minutes} min`),
        participants: info?.participantNames ?? [],
        summary: email.summary,
        actionItems: email.actionItems.map((item) => ({ text: item.text, assignee: item.assignee })),
      };
      const result = await sendSummaryEmail(payload, false, meetingId);
      if (result.ok) console.log('[botSync] özet e-postası gönderildi:', meetingId, `(${result.recipientCount} alıcı)`);
      else {
        if (result.reason !== 'disabled') console.warn('[botSync] özet e-postası gönderilemedi:', result.message);
        if (result.retryable) remaining.summaryEmail = email;
      }
    } catch (err) {
      console.error('[botSync] özet e-postası hatası:', err);
      remaining.summaryEmail = email;
    }
  }

  return remaining.participants || remaining.summaryEmail ? remaining : null;
}

async function retryPendingArchives(): Promise<void> {
  if (!isS3Configured()) return;
  for (const pending of await listPendingArchives()) {
    // Dosya bu makinede değilse (başka bilgisayarda kaydedilmiş) ya da kayıt
    // klasörünün dışını gösteriyorsa (SEC-07) atla.
    if (!(await safeRecordingPath(pending.audioFilePath))) continue;
    await archiveRecordedMeeting({
      meetingId: pending.meetingId,
      audioPath: pending.audioFilePath,
      transcript: pending.transcript,
      summary: pending.summary,
    });
  }
}
